import React, { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { FIELD, inr } from "./_shared";
import ReceiptField from "./ReceiptField";

/** Transport Expenses — Accounts logs trips with vendor/amount/notes and optional receipt.
 *  Visible + cost-visible: super_admin, accounts, admin. Others hidden. */
export default function TransportExpensesCard({ p, user }) {
  const role = user?.role;
  const canView = ["super_admin", "accounts", "admin"].includes(role);
  const canWrite = ["super_admin", "accounts"].includes(role);

  const [state, setState] = useState({ rows: [], total: 0 });
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: "", vendor: "", vehicle_no: "",
    from_location: "", to_location: "", notes: "", receipt: null,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/costing/project/${p.id}/transport-expenses`); setState(data); }
    catch { /* noop */ }
  };
  useEffect(() => { if (canView) load(); /* eslint-disable-next-line */ }, []);
  if (!canView) return null;

  const submit = async () => {
    if (!form.date || !form.amount) { toast.error("Date and amount are required"); return; }
    setSaving(true);
    try {
      await api.post(`/costing/project/${p.id}/transport-expenses`, {
        date: form.date, amount: Number(form.amount),
        vendor: form.vendor || null, vehicle_no: form.vehicle_no || null,
        from_location: form.from_location || null, to_location: form.to_location || null,
        notes: form.notes || null, receipt_file_id: form.receipt?.file_id || null,
      });
      toast.success("Transport expense added");
      setForm({ date: form.date, amount: "", vendor: "", vehicle_no: "",
                from_location: "", to_location: "", notes: "", receipt: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this transport expense?")) return;
    try { await api.delete(`/costing/project/${p.id}/transport-expenses/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="hk-card p-4" data-testid="transport-expenses-card">
      <div className="hk-tiny-label mb-2">Transport expenses</div>
      {canWrite && (
        <div className="border border-slate-200 rounded-sm p-2 mb-3 space-y-1">
          <div className="grid grid-cols-2 gap-1">
            <input className={FIELD} type="date" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="te-date" />
            <input className={FIELD} type="number" step="0.01" placeholder="Amount (₹)"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="te-amount" />
          </div>
          <input className={FIELD} placeholder="Vendor / transporter"
            value={form.vendor}
            onChange={(e) => setForm({ ...form, vendor: e.target.value })}
            data-testid="te-vendor" />
          <div className="grid grid-cols-3 gap-1">
            <input className={FIELD} placeholder="Vehicle no." value={form.vehicle_no}
              onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })}
              data-testid="te-vehicle" />
            <input className={FIELD} placeholder="From" value={form.from_location}
              onChange={(e) => setForm({ ...form, from_location: e.target.value })}
              data-testid="te-from" />
            <input className={FIELD} placeholder="To" value={form.to_location}
              onChange={(e) => setForm({ ...form, to_location: e.target.value })}
              data-testid="te-to" />
          </div>
          <input className={FIELD} placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="te-notes" />
          <ReceiptField p={p} receipt={form.receipt}
            onChange={(r) => setForm({ ...form, receipt: r })}
            required={false} testid="te-receipt" />
          <button type="button" onClick={submit} disabled={saving || !form.amount}
            data-testid="te-submit"
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-sm text-xs">
            {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Add trip
          </button>
        </div>
      )}
      {state.rows.length === 0 ? (
        <div className="text-xs text-slate-400" data-testid="te-empty">No transport expenses yet.</div>
      ) : (
        <>
          <ul className="space-y-1.5" data-testid="te-list">
            {state.rows.map((r) => (
              <li key={r.id} className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900">{r.vendor || "Trip"} · {inr(r.amount)}</div>
                    <div className="text-slate-600">
                      {r.date}
                      {r.vehicle_no && <> · {r.vehicle_no}</>}
                      {(r.from_location || r.to_location) &&
                        <> · {r.from_location || "?"} → {r.to_location || "?"}</>}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">By {r.user_name}</div>
                    {r.notes && <div className="text-slate-500 mt-0.5">{r.notes}</div>}
                    {r.receipt && (
                      <a href={`${api.defaults.baseURL}/costing/project/${p.id}/receipts/${r.receipt.file_id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-[10px] text-orange-700 hover:text-orange-900 underline mt-0.5 inline-block"
                        data-testid={`te-receipt-${r.id}`}>
                        📎 {r.receipt.name}
                      </a>
                    )}
                  </div>
                  {canWrite && (
                    <button type="button" onClick={() => del(r.id)}
                      data-testid={`te-del-${r.id}`}
                      className="text-red-600 hover:text-red-800 shrink-0" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between text-xs"
            data-testid="te-totals">
            <span className="font-bold text-slate-700">Trips: {state.rows.length}</span>
            <span className="font-bold text-slate-900">Total: {inr(state.total)}</span>
          </div>
        </>
      )}
    </div>
  );
}
